<template>
  <el-card class="shipping-addresses-card" shadow="lg">
    <template #header>
      <div class="card-header">
        <el-icon :size="24" style="margin-right: 10px; color: #67C23A;"><LocationInformation /></el-icon>
        <span>我的收貨地址</span>
        <el-button
          type="primary"
          size="small"
          @click="handleAddAddress"
          style="margin-left: auto;"
        >
          新增地址
        </el-button>
      </div>
    </template>

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>

    <div v-else-if="error" class="error-message-container">
      <el-result
        icon="error"
        title="地址加載失敗"
      >
        <template #extra>
          <el-button type="primary" @click="fetchAddresses">重試</el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="addresses.length > 0">
      <el-table :data="addresses" style="width: 100%" stripe border>
        <el-table-column prop="recipient" label="收貨人" min-width="100" />
        <el-table-column prop="phone" label="電話" min-width="120" />
        <el-table-column prop="email" label="郵箱" min-width="180" show-overflow-tooltip />
        <el-table-column prop="address" label="詳細地址" min-width="250" show-overflow-tooltip />
        <el-table-column label="默認" width="80" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.is_default" type="success" size="small">是</el-tag>
            <el-button v-else type="text" size="small" @click="setDefaultAddress(scope.row)">設為默認</el-button>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              link
              icon="Edit"
              @click="handleEditAddress(scope.row)"
            >
              修改
            </el-button>
            <el-popconfirm
                title="確定要刪除這個地址嗎?"
                confirm-button-text="確定"
                cancel-button-text="取消"
                @confirm="handleDeleteAddress(scope.row.id)"
              >
              <template #reference>
                <el-button
                  size="small"
                  type="danger"
                  link
                  icon="Delete"
                >
                  刪除
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div v-else class="empty-addresses">
      <el-empty description="您還沒有添加任何收貨地址">
        <el-button type="primary" @click="handleAddAddress">立即新增</el-button>
      </el-empty>
    </div>

    <!-- 新增/編輯地址的對話框 (示例) -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEditMode ? '修改收貨地址' : '新增收貨地址'"
      width="600px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form ref="addressFormRef" :model="currentAddress" :rules="addressRules" label-width="100px">
        <el-form-item label="收貨人" prop="recipient">
          <el-input v-model="currentAddress.recipient" placeholder="請輸入收貨人姓名"></el-input>
        </el-form-item>
        <el-form-item label="手機號碼" prop="phone">
          <el-input v-model="currentAddress.phone" placeholder="請輸入手機號碼"></el-input>
        </el-form-item>
        <el-form-item label="電子郵箱" prop="email">
          <el-input v-model="currentAddress.email" placeholder="請輸入電子郵箱 (可選)"></el-input>
        </el-form-item>
        <el-form-item label="詳細地址" prop="address">
          <el-input v-model="currentAddress.address" type="textarea" :rows="3" placeholder="請輸入詳細收貨地址"></el-input>
        </el-form-item>
         <el-form-item label="設為默認">
          <el-switch v-model="currentAddress.is_default"></el-switch>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitAddressForm">確定</el-button>
        </span>
      </template>
    </el-dialog>

  </el-card>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { LocationInformation, Plus, Edit, Delete } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus'; // 確保引入
import AddressServer from '../../../services/AddressManage'

// 模擬數據和狀態
const loading = ref(true);
const error = ref(null);
const addresses = ref([]); // 收貨地址列表

const fetchAddresses = async () => {
  loading.value = true;
  error.value = null;
  try {
    const data = await AddressServer.getAddress();
    addresses.value = data;
  } catch (err) {
    error.value = err.message || err;
    ElMessage.error('獲取收貨地址失敗');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchAddresses();
});

// --- 新增/編輯對話框邏輯 ---
const dialogVisible = ref(false);
const isEditMode = ref(false);
const addressFormRef = ref(null);
const initialAddressData = {
  recipient: '',
  phone: '',
  email: '',
  address: '',
  is_default: false,
};
const currentAddress = reactive({ ...initialAddressData });

const addressRules = {
  recipient: [{ required: true, message: '請輸入收貨人姓名', trigger: 'blur' }],
  phone: [
    { required: true, message: '請輸入手機號碼', trigger: 'blur' },
  ],
  email: [
    { type: 'email', message: '郵箱格式不正確', trigger: ['blur', 'change'] }
  ],
  address: [{ required: true, message: '請輸入詳細地址', trigger: 'blur' }],
};

const resetForm = () => {
  Object.assign(currentAddress, initialAddressData);
  isEditMode.value = false;
  if (addressFormRef.value) {
    addressFormRef.value.resetFields();
  }
};

const handleAddAddress = () => {
  resetForm();
  dialogVisible.value = true;
};

const handleEditAddress = (address) => {
  resetForm(); // 先重置
  Object.assign(currentAddress, JSON.parse(JSON.stringify(address))); // 深拷貝賦值
  isEditMode.value = true;
  dialogVisible.value = true;
};

const submitAddressForm = async () => {
  if (!addressFormRef.value) return;
  await addressFormRef.value.validate(async (valid) => {
    if (valid) {
      // 保存
      if (isEditMode.value) {
        // 修改邏輯
        const index = addresses.value.findIndex(a => a.id === currentAddress.id);
        if (index !== -1) {
          if (currentAddress.is_default) { // 如果設為默認，其他地址取消默認
            addresses.value.forEach(addr => addr.is_default = false);
          }
          addresses.value.splice(index, 1, { ...currentAddress }); // 替換
          const result = await AddressServer.updateAddress(currentAddress)
          if (result){
            fetchAddresses();
            ElMessage.success('地址修改成功！');
          }
        }
      } else {
        // 新增邏輯
        if (currentAddress.is_default) { // 如果設為默認，其他地址取消默認
            addresses.value.forEach(addr => addr.is_default = false);
        }
        const result = await AddressServer.createAddress(currentAddress)
        if (result){
            fetchAddresses();
            ElMessage.success('地址新增成功！');
        }
      }
      dialogVisible.value = false;
    } else {
      ElMessage.error('表單校驗失敗，請檢查輸入！');
      return false;
    }
  });
};

const handleDeleteAddress = (addressId) => {
  // 模擬刪除
  addresses.value = addresses.value.filter(address => address.id !== addressId);
  AddressServer.deleteAddress(addressId)
  // 如果刪除的是默認地址，需要處理（例如選擇第一個作為新的默認，或提示用戶設置）
  if (!addresses.value.find(a => a.is_default) && addresses.value.length > 0) {
    addresses.value[0].is_default = true;
    AddressServer.updateStatus(addresses.value[0].id)
  }
  ElMessage.success('地址刪除成功！');
  // 在實際應用中，這裡會調用 API 刪除數據

};

const setDefaultAddress = (addressToSetDefault) => {
    try{
        addresses.value.forEach(addr => {
            addr.is_default = (addr.id === addressToSetDefault.id);
        });
        AddressServer.updateStatus(addressToSetDefault.id)
        ElMessage.success(`已將 ${addressToSetDefault.recipient} 的地址設為默認`);
        // 在實際應用中，這裡會調用 API 更新默認地址狀態
    }catch(err){
        ElMessage.error('設置失敗');
    }
};

</script>

<style scoped>
.shipping-addresses-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--el-text-color-regular);
  padding: 5px 0; /* 調整頭部內邊距 */
}

.loading-container,
.error-message-container,
.empty-addresses {
  padding: 40px 20px;
  text-align: center;
}
.error-message-container .el-result {
    padding: 0;
}

:deep(.el-table th.el-table__cell) {
  background-color: #fafafa;
  color: #303133;
}
:deep(.el-table td.el-table__cell .el-button--small.is-link) {
  padding: 0 5px;
}
.dialog-footer {
  text-align: right;
}
</style>